import { DateFormat, NumberFormat } from '@insio/shared-types';
import React, { createContext, ReactNode, useContext } from 'react';

const DEFAULT_LOCALE = 'en';
const DEFAULT_TIME_ZONE = 'Europe/Prague';

interface UIContextValue {
  i18n: { t: (key: string, params?: Record<string, unknown>) => string };
  /** BCP-47 tag for UI translations (localization). Drives `t()` via the host app. */
  locale: string;
  /** IANA time zone identifier. UTC dates are converted to this zone for display. */
  timeZone: string;
  /** ISO 4217 accounting (local) currency code resolved from the account main party. */
  accountingCurrencyCode: string | null;
  /** ISO 4217 reporting currency code resolved from the account main party. */
  reportingCurrencyCode: string | null;
  /** Date display format pattern (CLDR / date-fns notation, e.g. `d.M.yyyy`). */
  dateFormat: string;
  /** Number format token (decimal/thousands separator, e.g. `COMMA_SPACE`). */
  numberFormat: string;
}

const UIContext = createContext<UIContextValue>({
  i18n: { t: () => '' },
  locale: DEFAULT_LOCALE,
  timeZone: DEFAULT_TIME_ZONE,
  accountingCurrencyCode: null,
  reportingCurrencyCode: null,
  dateFormat: DateFormat.D_M_YYYY_DOT,
  numberFormat: NumberFormat.COMMA_SPACE
});

export interface UIProviderProps {
  children: ReactNode;
  i18n: { t: (key: string, params?: Record<string, unknown>) => string };
  /** BCP-47 tag for translations. Defaults to 'en'. */
  locale?: string;
  /** Date display format (CLDR / date-fns notation). Defaults to `DateFormat.D_M_YYYY_DOT`. */
  dateFormat?: string;
  /** Number format token (decimal/thousands separator). Defaults to `NumberFormat.COMMA_SPACE`. */
  numberFormat?: string;
  /** IANA time zone. Defaults to 'Europe/Prague'. */
  timeZone?: string;
  /** ISO 4217 accounting (local) currency code from the account main party. */
  accountingCurrencyCode?: string | null;
  /** ISO 4217 reporting currency code from the account main party. */
  reportingCurrencyCode?: string | null;
}

export const UIProvider: React.FC<UIProviderProps> = ({
  children,
  i18n,
  locale = DEFAULT_LOCALE,
  dateFormat = DateFormat.D_M_YYYY_DOT,
  numberFormat = NumberFormat.COMMA_SPACE,
  timeZone = DEFAULT_TIME_ZONE,
  accountingCurrencyCode = null,
  reportingCurrencyCode = null
}) => (
  <UIContext.Provider
    value={{
      i18n,
      locale,
      timeZone,
      accountingCurrencyCode,
      reportingCurrencyCode,
      dateFormat,
      numberFormat
    }}
  >
    {children}
  </UIContext.Provider>
);

export const useUI = (): UIContextValue => useContext(UIContext);

export const useLocale = (): string => useContext(UIContext).locale;

export const useTimeZone = (): string => useContext(UIContext).timeZone;
export const useDateFormat = (): string => useContext(UIContext).dateFormat;
export const useNumberFormat = (): string => useContext(UIContext).numberFormat;

export const useAccountingCurrencyCode = (): string | null =>
  useContext(UIContext).accountingCurrencyCode;

export const useReportingCurrencyCode = (): string | null =>
  useContext(UIContext).reportingCurrencyCode;

// Translation hook with optional prefix support
export const useTranslation = (prefix?: string) => {
  const { i18n } = useContext(UIContext);

  if (prefix) {
    return (key: string, params?: Record<string, unknown>) => i18n.t(`${prefix}.${key}`, params);
  }

  return i18n.t;
};
